'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Brain, BarChart2, Dumbbell, Sparkles, Home, Users, Shield, Bell, FlaskConical, Layers, TrendingUp, Activity, FileText } from 'lucide-react';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  matchPaths?: string[];
  tabParam?: string;
}

interface MobileBottomNavProps {
  role?: string | null;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

const PATIENT_NAV: NavItem[] = [
  { label: 'Home', href: '/patient-dashboard', icon: <Home size={20} />, matchPaths: ['/patient-dashboard'], tabParam: 'home' },
  { label: 'Train', href: '/patient-dashboard', icon: <Dumbbell size={20} />, matchPaths: ['/patient-dashboard'], tabParam: 'train' },
  { label: 'Progress', href: '/patient-dashboard', icon: <BarChart2 size={20} />, matchPaths: ['/patient-dashboard'], tabParam: 'progress' },
  { label: 'Reports', href: '/patient-dashboard', icon: <FileText size={20} />, matchPaths: ['/patient-dashboard'], tabParam: 'reports' },
  { label: 'Quiz', href: '/patient-dashboard', icon: <Sparkles size={20} />, matchPaths: ['/patient-dashboard'], tabParam: 'quiz' },
];

const CLINICIAN_NAV: NavItem[] = [
  { label: 'Dashboard', href: '/clinician-portal', icon: <Home size={20} />, matchPaths: ['/clinician-portal'] },
  { label: 'EEG', href: '/eeg-cognitive-dashboard', icon: <Brain size={20} />, matchPaths: ['/eeg-cognitive-dashboard'] },
  { label: 'Patients', href: '/patient-progress', icon: <Users size={20} />, matchPaths: ['/patient-progress'] },
  { label: 'Alerts', href: '/biomarker-alerts', icon: <Bell size={20} />, matchPaths: ['/biomarker-alerts'] },
  { label: 'Protocols', href: '/intervention-protocols', icon: <FlaskConical size={20} />, matchPaths: ['/intervention-protocols'] },
];

const ADMIN_NAV: NavItem[] = [
  { label: 'Admin', href: '/admin-dashboard', icon: <Shield size={20} />, matchPaths: ['/admin-dashboard'] },
  { label: 'EEG', href: '/eeg-cognitive-dashboard', icon: <Brain size={20} />, matchPaths: ['/eeg-cognitive-dashboard'] },
  { label: 'Analytics', href: '/cohort-analytics', icon: <BarChart2 size={20} />, matchPaths: ['/cohort-analytics'] },
  { label: 'Cohorts', href: '/cohort-management', icon: <Layers size={20} />, matchPaths: ['/cohort-management'] },
  { label: 'Alerts', href: '/biomarker-alerts', icon: <Bell size={20} />, matchPaths: ['/biomarker-alerts'] },
];

const RESEARCHER_NAV: NavItem[] = [
  { label: 'Research', href: '/researcher-portal', icon: <Home size={20} />, matchPaths: ['/researcher-portal'] },
  { label: 'Cohorts', href: '/cohort-analytics', icon: <BarChart2 size={20} />, matchPaths: ['/cohort-analytics'] },
  { label: 'Manage', href: '/cohort-management', icon: <Layers size={20} />, matchPaths: ['/cohort-management'] },
  { label: 'Progress', href: '/patient-progress', icon: <TrendingUp size={20} />, matchPaths: ['/patient-progress'] },
  { label: 'Wearable', href: '/wearable-integration', icon: <Activity size={20} />, matchPaths: ['/wearable-integration'] },
];

const DEFAULT_NAV: NavItem[] = [
  { label: 'Dashboard', href: '/eeg-cognitive-dashboard', icon: <Brain size={20} />, matchPaths: ['/eeg-cognitive-dashboard'] },
  { label: 'Analytics', href: '/cohort-analytics', icon: <BarChart2 size={20} />, matchPaths: ['/cohort-analytics'] },
  { label: 'Patients', href: '/patient-progress', icon: <Users size={20} />, matchPaths: ['/patient-progress'] },
  { label: 'Alerts', href: '/biomarker-alerts', icon: <Bell size={20} />, matchPaths: ['/biomarker-alerts'] },
];

export default function MobileBottomNav({ role, activeTab, onTabChange }: MobileBottomNavProps) {
  const pathname = usePathname();

  const navItems = role === 'patient'
    ? PATIENT_NAV
    : role === 'clinician'
    ? CLINICIAN_NAV
    : role === 'admin'
    ? ADMIN_NAV
    : role === 'researcher'
    ? RESEARCHER_NAV
    : DEFAULT_NAV;

  return (
    <nav
      className="mobile-bottom-nav"
      aria-label={`${role || 'Main'} navigation`}
      role="navigation"
    >
      {navItems.map((item, index) => {
        let isActive = false;

        if (role === 'patient' && item.tabParam) {
          // For patient tabs, match against the activeTab prop
          isActive = activeTab === item.tabParam || (!activeTab && item.tabParam === 'home');
        } else {
          isActive = item.matchPaths && item.matchPaths.length > 0
            ? item.matchPaths.some(p => pathname === p || pathname?.startsWith(p + '/'))
            : false;
        }

        // For patient nav items, use button to trigger tab change
        if (role === 'patient' && item.tabParam && onTabChange) {
          return (
            <button
              key={`${item.tabParam}-${index}`}
              onClick={() => onTabChange(item.tabParam!)}
              className={`mobile-bottom-nav-item touch-manipulation relative ${isActive ? 'active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
              aria-label={item.label}
              style={{ minHeight: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <span className={`transition-transform duration-200 ${isActive ? 'scale-110' : ''}`} aria-hidden="true">
                {item.icon}
              </span>
              <span className="text-[10px] font-medium leading-none">{item.label}</span>
              {isActive && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full bg-[#F97316]" aria-hidden="true" />
              )}
            </button>
          );
        }

        return (
          <Link
            key={`${item.href}-${index}`}
            href={item.href}
            className={`mobile-bottom-nav-item touch-manipulation relative ${isActive ? 'active' : ''}`}
            aria-current={isActive ? 'page' : undefined}
            aria-label={item.label}
          >
            <span className={`transition-transform duration-200 ${isActive ? 'scale-110' : ''}`} aria-hidden="true">
              {item.icon}
            </span>
            <span className="text-[10px] font-medium leading-none">{item.label}</span>
            {isActive && (
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full bg-[#F97316]" aria-hidden="true" />
            )}
          </Link>
        );
      })}
    </nav>
  );
}
